import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom'; // Ajouté
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { routesData, getPopularRoutes } from '../data/routesData';

import heroBus from '../assets/images/buses/bus-1.jpeg';
import busImage2 from '../assets/images/buses/bus-2.jpeg';
import busImage3 from '../assets/images/buses/bus-1.jpeg';

export default function Home() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate(); // Ajouté
  const [search, setSearch] = useState({ from: '', to: '', date: '' });

  const currentLang = i18n.language;

  // Villes de départ uniques
  const departureCities = useMemo(() => {
    const set = new Set(routesData.map(r => r.departure));
    return Array.from(set).sort();
  }, []);

  // Villes d'arrivée filtrées
  const arrivalCities = useMemo(() => {
    const filtered = search.from
      ? routesData.filter(r => r.departure === search.from)
      : routesData;

    const set = new Set(filtered.map(r => r.arrival));
    return Array.from(set).sort();
  }, [search.from]);

  // Images rotatives pour les destinations populaires
  const busImages = [heroBus, busImage2, busImage3];
  
  const popularRoutes = getPopularRoutes(6);
  const destinations = popularRoutes.map((route, index) => ({
    from: route.departure,
    to: route.arrival,
    price: route.price ?? 0,
    duration: route.duration || '—',
    image: busImages[index % busImages.length],
  }));

  // Fonction pour naviguer vers la réservation
  const handleBook = (departure, arrival) => {
    const today = new Date().toISOString().split('T')[0];
    const params = new URLSearchParams({
      from: departure,
      to: arrival,
      date: today,
    });
    navigate(`/booking?${params.toString()}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!search.from || !search.to || !search.date) return;

    const params = new URLSearchParams({
      from: search.from,
      to: search.to,
      date: search.date,
    });

    navigate(`/booking?${params.toString()}`);
  };

  return (
    <div className={`${currentLang === 'ar' ? 'rtl' : ''} bg-neutral-50`}>
      {/* HERO SECTION */}
      <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative min-h-[600px] flex items-center justify-center overflow-hidden"
          style={{
            backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.4), rgba(30, 64, 175, 0.6)), url(${heroBus})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <motion.h1
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className="text-5xl md:text-6xl font-display font-bold mb-4"
            >
              {t('hero_title')}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-xl md:text-2xl mb-8 font-light"
            >
              {t('hero_subtitle')}
            </motion.p>

            {/* FORMULAIRE DE RECHERCHE */}
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, type: 'spring' }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-2xl"
            >
              <form
                onSubmit={handleSearch}
                className="grid grid-cols-1 md:grid-cols-4 gap-4"
              >
                {/* DEPART */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-neutral-900 mb-2">
                    {t('search_departure')}
                  </label>
                  <select
                    className="w-full p-4 border-2 border-neutral-100 rounded-xl focus:border-primary-600 focus:outline-none transition text-neutral-900 bg-white"
                    value={search.from}
                    onChange={(e) =>
                      setSearch({ ...search, from: e.target.value, to: '' })
                    }
                    required
                  >
                    <option value="">
                      {currentLang === 'ar'
                        ? 'اختر مدينة الانطلاق'
                        : 'Choisissez le départ'}
                    </option>
                    {departureCities.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ARRIVEE */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-neutral-900 mb-2">
                    {t('search_arrival')}
                  </label>
                  <select
                    className="w-full p-4 border-2 border-neutral-100 rounded-xl focus:border-primary-600 focus:outline-none transition text-neutral-900 bg-white"
                    value={search.to}
                    onChange={(e) =>
                      setSearch({ ...search, to: e.target.value })
                    }
                    required
                    disabled={!search.from}
                  >
                    <option value="">
                      {search.from
                        ? currentLang === 'ar'
                          ? 'اختر وجهتك'
                          : 'Choisissez la destination'
                        : currentLang === 'ar'
                        ? 'اختر اولا مدينة الانطلاق'
                        : 'Choisissez d’abord le départ'}
                    </option>
                    {arrivalCities.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>

                {/* DATE */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-neutral-900 mb-2">
                    {t('search_date')}
                  </label>
                  <input
                    type="date"
                    className="w-full p-4 border-2 border-neutral-100 rounded-xl focus:border-primary-600 focus:outline-none transition text-neutral-900 bg-white"
                    value={search.date}
                    onChange={(e) =>
                      setSearch({ ...search, date: e.target.value })
                    }
                    required
                  />
                </div>

                {/* BOUTON RECHERCHE */}
                <div className="flex items-end">
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-accent-500 to-accent-400 text-white font-bold p-4 rounded-xl hover:from-accent-600 hover:to-accent-500 transition transform hover:scale-105 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={!search.from || !search.to || !search.date}
                  >
                    {t('search_button')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* DESTINATIONS POPULAIRES - MODIFIÉES */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-display font-bold text-neutral-900 mb-4">
              {t('popular_destinations')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('popular_destinations_desc')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {destinations.map((dest, i) => (
              <motion.div
                key={`${dest.from}-${dest.to}-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, type: 'spring' }}
                whileHover={{ y: -8 }}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition cursor-pointer"
                onClick={() => handleBook(dest.from, dest.to)} // CARTE CLIQUABLE
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={dest.image}
                    alt={`${dest.from} ${dest.to}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>

                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-display font-bold text-xl text-neutral-900 mb-1">
                        {dest.from} → {dest.to}
                      </h3>
                      <p className="text-gray-500 text-sm">🕐 {dest.duration}</p>
                    </div>
                    <span className="bg-accent-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      POPULAIRE
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <p className="text-3xl font-bold text-primary-600">
                      {(dest.price ?? 0).toLocaleString()}
                      <span className="text-lg text-gray-500 ml-1">XAF</span>
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Empêche le double déclenchement
                        handleBook(dest.from, dest.to);
                      }}
                      className="bg-primary-600 text-white px-4 py-2 rounded-full font-semibold hover:bg-primary-700 transition transform hover:scale-105"
                    >
                      Réserver {/* REMPLACÉ */}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* RESTE DU CODE INCHANGÉ */}
      {/* AVANTAGES */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-display font-bold text-neutral-900 mb-4">
              {t('why_choose_us')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('services_designed_for_comfort')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[t('security'), t('punctuality'), t('comfort')].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                whileHover={{
                  y: -10,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                }}
                className="text-center p-8 rounded-2xl bg-neutral-50 hover:bg-white hover:shadow-xl transition"
              >
                <h3 className="font-display font-bold text-2xl text-neutral-900 mb-4">
                  {item}
                </h3>
                <p className="text-gray-600 leading-relaxed">{t(`${Object.keys({security:'', punctuality:'', comfort:''})[i]}_desc`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 bg-gradient-to-r from-primary-900 to-primary-600 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: '20+', label: t('fleet_buses') },
              { number: '15', label: t('destinations_count') },
              { number: '5K+', label: t('passengers_month') },
              { number: '98%', label: t('satisfaction_rate') },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="p-4 rounded-2xl bg-white/10 hover:bg-white/20 transition"
              >
                <div className="text-4xl font-display font-bold mb-2">
                  {stat.number}
                </div>
                <p className="text-lg opacity-90">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-accent-500 to-accent-400 rounded-3xl p-12 text-center text-white shadow-2xl mx-auto max-w-4xl"
          >
            <h2 className="text-3xl font-display font-bold mb-4">
              {t('ready_to_travel')}
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              {t('ready_to_travel_desc')}
            </p>
            <motion.button
              onClick={() => navigate('/booking')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-accent-500 font-bold px-8 py-4 rounded-full text-lg shadow-lg"
            >
              {t('book_now')}
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}